import React, { useEffect, useMemo, useState } from "react";
import { View, Text, StyleSheet } from "react-native";
import { Paper } from "@mui/material";
import { useLazyQuery, useQuery } from "@apollo/client";
import { ModalV3, SingleSelectV3 } from "../../../../common/component";
import TextInputV3 from "../../../../common/component/TextInputV3";
import { FONT_FAMILY, THEME_COLORS } from "../../../../common/themes";
import { Chip, Divider } from "react-native-elements";
import { ADD_CAR_CHILD_WORKSHOPS, GET_CUSTOMER_LIST_V3, GET_ADVISORS_FOR_WORKSHOP_V3 } from "../query";
import { client, clientExternalV2, clientV2 } from "../../../../common";
import { isWarrantyEnabled } from "@src/utils/functions";

export interface AddCarInputProps {
  leadId: string;
  tradingPartnerId: string;
  refurbCategory: string;
  workshopId: string;
  cgtUserId: string;
  ticketId: string;
}

interface AddCarModalProps {
  visible: boolean;
  onClose: () => void;
  onAddCar: (arg: AddCarInputProps) => any;
  loading?: boolean;
}

var workTypes = [
  {
    displayName: "Refurb",
    id: "REFURB",
  },
  {
    displayName: "5DMB",
    id: "FIVE_DAY_MONEY_BACK",
  },
  {
    displayName: "Insurance",
    id: "INSURANCE",
  },
  {
    displayName: "Post Sale: B2C",
    id: "POST_SALE_B2C",
  },
];

var postSaleWorkTypes = [
  {
    displayName: "Warranty",
    id: "POST_SALE_WARRANTY",
  },
  {
    displayName: "Post Sale: B2C",
    id: "POST_SALE_B2C",
  },
];

if (isWarrantyEnabled?.() && false) {
  workTypes = workTypes?.filter?.((val) => val?.id !== "POST_SALE_WARRANTY");
  postSaleWorkTypes = postSaleWorkTypes?.filter?.((val) => val?.id !== "POST_SALE_WARRANTY");
}

function AddCarModal({ visible, onClose, onAddCar, loading }: AddCarModalProps) {
  const [leadId, setLeadId] = useState("");
  const [workshopId, setWorkshopId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [workType, setWorkType] = useState(workTypes[0].id);
  const [cgt, setCGT] = useState("");
  const [ticketNumber, setTicketNumber] = useState("");
  const buttonDisabled = useMemo(() => {
    return (
      customerId == "" ||
      workshopId == "" ||
      leadId == "" ||
      cgt == "" ||
      customerId == "" ||
      (postSaleWorkTypes.some((val) => val.id === workType) && ticketNumber == "")
    );
  }, [workshopId, cgt, leadId, customerId, ticketNumber, workType]);
  const { data: customersData } = useQuery(GET_CUSTOMER_LIST_V3, {
    client: clientExternalV2,
    fetchPolicy: "cache-first",
  });
  const { data: workshops } = useQuery(ADD_CAR_CHILD_WORKSHOPS, {
    client: clientV2,
    fetchPolicy: "cache-and-network",
  });
  const [getAdvisors, { data: advisors }] = useLazyQuery(GET_ADVISORS_FOR_WORKSHOP_V3, {
    client: clientV2,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (visible) resetState();
  }, [visible]);

  const onSelectWorkshop = (val) => {
    getAdvisors({ variables: { workshopIds: [val] } });
    setWorkshopId(val);
    setCGT("");
  };

  const resetState = () => {
    setLeadId("");
    setWorkshopId("");
    setCustomerId("");
    setCGT("");
  };

  const onCloseModal = () => {
    resetState();
    onClose();
  };

  return (
    <ModalV3
      visible={visible}
      header={"Add New Car"}
      subheader={"Add car that has not been added in upcoming inventory"}
      onCancel={onCloseModal}
      onConfirm={() =>
        onAddCar({
          leadId,
          tradingPartnerId: customerId,
          refurbCategory: workType,
          workshopId,
          cgtUserId: cgt,
          ticketId: ticketNumber,
        })
      }
      confirmButtonText="Add car"
      disabledConfirm={buttonDisabled || loading}
      disabledCancel={loading}
    >
      <Paper elevation={6} style={{ marginTop: 40, paddingTop: 24 }}>
        <Text style={styles.header}>Car Details</Text>
        <View style={styles.formInputs}>
          <Text>Lead ID</Text>
          <TextInputV3
            autoFocus
            placeholder="Enter Lead ID"
            variant="outlined"
            size="small"
            value={leadId}
            onChange={(e) => setLeadId(e.target.value)}
            inputProps={{ style: { textAlign: "right" } }}
            style={{ width: "20%", minWidth: 150 }}
          />
        </View>
        <Divider style={{ marginHorizontal: 24 }} />
        <View style={styles.formInputs}>
          <Text>Customer</Text>
          <SingleSelectV3
            style={{ marginTop: "0.2em" }}
            options={customersData?.customerList || []}
            placeholder="Select Customer"
            noOutline
            value={customerId}
            setValue={(val) => setCustomerId(val)}
            labelExtractor="displayName"
            valueExtractor="id"
            containerStyle={{ maxWidth: "75%", width: "fit-content" }}
          />
        </View>
        <Divider style={{ marginHorizontal: 24 }} />
        <View style={styles.formInputs}>
          <Text>Refurb Type</Text>
          <View style={{ flexDirection: "row" }}>
            {workTypes.map((type) => (
              <Chip
                title={type.displayName}
                key={type.id}
                type="outline"
                onPress={() => setWorkType(type.id)}
                buttonStyle={[
                  styles.chip,
                  { marginEnd: 10, backgroundColor: workType == type.id ? THEME_COLORS.purple : THEME_COLORS.white },
                ]}
                titleStyle={[styles.text, { color: workType == type.id ? THEME_COLORS.white : THEME_COLORS.purple }]}
              />
            ))}
          </View>
        </View>
        <Divider style={{ marginHorizontal: 24 }} />
        <View style={styles.formInputs}>
          <Text>Workshop</Text>
          <SingleSelectV3
            style={{ marginTop: "0.2em" }}
            options={workshops?.getChildWorkshops || []}
            placeholder="Select Workshop"
            noOutline
            value={workshopId}
            setValue={onSelectWorkshop}
            labelExtractor="displayName"
            valueExtractor="id"
            containerStyle={{ maxWidth: "75%", width: "fit-content" }}
          />
        </View>
        <Divider style={{ marginHorizontal: 24 }} />
        <View style={styles.formInputs}>
          <Text>CGT</Text>
          <SingleSelectV3
            style={{ marginTop: "0.2em", opacity: workshopId == "" && !advisors?.getAdvisorsForWorkshop ? 0.5 : 1 }}
            options={advisors?.getAdvisorsForWorkshop || []}
            placeholder="Select CGT"
            noOutline
            value={cgt}
            setValue={(val) => setCGT(val)}
            labelExtractor="fullName"
            valueExtractor="id"
            disabled={workshopId == "" && !advisors?.getAdvisorsForWorkshop}
            containerStyle={{ maxWidth: "75%", width: "fit-content" }}
          />
        </View>
        {postSaleWorkTypes.some((val) => val.id === workType) ? (
          <View>
            <Divider style={{ marginHorizontal: 24 }} />
            <View style={styles.formInputs}>
              <Text>Ticket Number</Text>
              <TextInputV3
                autoFocus
                placeholder="Ticket Number"
                variant="outlined"
                size="small"
                value={ticketNumber}
                onChange={(e) => setTicketNumber(e.target.value)}
                inputProps={{ style: { textAlign: "right" }, maxLength: 8 }}
                style={{ width: "30%", minWidth: 150 }}
              />
            </View>
            <Text
              style={{
                marginBottom: 40,
                marginEnd: 20,
                fontWeight: "700",
                fontSize: 12,
                color: "black",
                alignSelf: "flex-end",
              }}
            >
              Please Enter 8 Digit Ticket Number Ex: 00123456
            </Text>
          </View>
        ) : null}
      </Paper>
    </ModalV3>
  );
}

const styles = StyleSheet.create({
  header: {
    fontWeight: "bold",
    fontFamily: FONT_FAMILY.semiBoldV3,
    marginHorizontal: 16,
    marginVertical: 32,
  },
  text: {
    fontFamily: FONT_FAMILY.primaryV3,
  },
  formInputs: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 16,
    justifyContent: "space-between",
  },
  chip: {
    borderColor: THEME_COLORS.purple,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
});

export default AddCarModal;
